import { BRAND } from "../constants/brand";
import PageHero from "../components/PageHero";

const LICENSES = [
  {
    acronym: "CAP",
    name: "College of American Pathologists",
    type: "Accreditation",
    holder: "Med-Lake Diagnostic Services / Labarium Diagnostic Systems",
    description: "CAP accreditation is the gold standard in laboratory quality assurance. CAP-accredited laboratories voluntarily participate in rigorous proficiency testing and submit to on-site inspections by trained peer reviewers. Fewer than half of all US laboratories meet this standard.",
    color: BRAND.navy,
    badge: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M10 16l4 4 8-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    acronym: "CLIA",
    name: "Clinical Laboratory Improvement Amendments",
    type: "Federal Certification",
    holder: "Med-Lake Diagnostic Services / Labarium Diagnostic Systems",
    description: "CLIA certification is required by federal law (42 U.S.C. §263a) for any facility that performs testing on human specimens for the purpose of diagnosis, prevention, or treatment of disease. Our CLIA certificate authorizes the performance of high-complexity testing.",
    color: "#002560",
    badge: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <rect x="4" y="4" width="24" height="24" rx="4" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M11 16h10M16 11v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    acronym: "GA",
    name: "Georgia State Laboratory License",
    type: "State License",
    holder: "Med-Lake Diagnostic Services — Milledgeville, GA",
    description: "Our Georgia state laboratory license authorizes operation of clinical laboratory services within the state of Georgia under the Georgia Department of Community Health. This license is maintained in active and compliant status at our Milledgeville, GA facility.",
    color: "#1a4a6e",
    badge: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <path d="M16 3C10.48 3 6 7.48 6 13c0 7 10 17 10 17s10-10 10-17c0-5.52-4.48-10-10-10z" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="16" cy="13" r="3" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
  },
];

export default function LicensesPage() {
  return (
    <>
      <PageHero
        eyebrow="COMPLIANCE"
        title="Our"
        highlight="Licenses"
        subtitle="Labarium Diagnostic Systems and its subsidiaries maintain all required accreditations, certifications, and state licenses to operate as a high-complexity clinical laboratory."
      />

      <section className="rx-pad" style={{ background: BRAND.offWhite, padding: "80px 40px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>

          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <div className="reveal" style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
              <div style={{ width: 48, height: 1, background: BRAND.gold }} />
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.28em", color: BRAND.gold, textTransform: "uppercase" }}>Accreditations & Certifications</span>
              <div style={{ width: 48, height: 1, background: BRAND.gold }} />
            </div>
            <h2 className="reveal delay-100" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "clamp(26px,3.5vw,42px)", color: BRAND.navy, margin: "0 0 16px" }}>
              Med-Lake · Labarium Licenses
            </h2>
            <p className="reveal delay-200" style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: BRAND.textMid, maxWidth: 580, margin: "0 auto", lineHeight: 1.75 }}>
              Certificates are available upon request. Additional laboratory licenses will be listed here as the Labarium network expands.
            </p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(520px, 1fr))", gap: 28 }}>
            {LICENSES.map((lic, i) => (
              <div
                key={i}
                className={`reveal delay-${(i + 1) * 100}`}
                style={{
                  background: BRAND.white,
                  borderRadius: 20,
                  border: `1px solid ${BRAND.lightGray}`,
                  overflow: "hidden",
                  boxShadow: "0 4px 20px rgba(0,53,122,0.05)",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 20px 56px rgba(0,30,80,0.12)"; e.currentTarget.style.borderColor = "rgba(201,169,110,0.3)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,53,122,0.05)"; e.currentTarget.style.borderColor = BRAND.lightGray; }}
              >
                {/* Card header */}
                <div style={{ background: lic.color, padding: "28px 32px", position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldLight}, ${BRAND.gold})` }} />
                  <div style={{ position: "absolute", bottom: -30, right: -30, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                    <div style={{
                      width: 64, height: 64, borderRadius: 16,
                      background: "rgba(201,169,110,0.15)",
                      border: "1.5px solid rgba(201,169,110,0.3)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: BRAND.gold, flexShrink: 0,
                    }}>
                      {lic.badge}
                    </div>
                    <div>
                      <div style={{
                        fontFamily: "'Playfair Display', serif", fontWeight: 800,
                        fontSize: 36, color: BRAND.gold, lineHeight: 1,
                        letterSpacing: "-0.01em",
                      }}>
                        {lic.acronym}
                      </div>
                      <div style={{
                        fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600,
                        letterSpacing: "0.12em", color: "rgba(255,255,255,0.5)",
                        textTransform: "uppercase", marginTop: 4,
                      }}>
                        {lic.type}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card body */}
                <div style={{ padding: "28px 32px 32px" }}>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 19, color: BRAND.navy, margin: "0 0 10px", lineHeight: 1.3 }}>
                    {lic.name}
                  </h3>
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: "rgba(0,53,122,0.06)",
                    borderRadius: 6, padding: "4px 10px",
                    marginBottom: 16,
                  }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke={BRAND.navy} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, color: BRAND.navy, letterSpacing: "0.06em" }}>{lic.holder}</span>
                  </div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: BRAND.textMid, lineHeight: 1.8, margin: "0 0 20px" }}>
                    {lic.description}
                  </p>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8,
                    fontFamily: "'Inter', sans-serif", fontSize: 12,
                    color: BRAND.gold, fontWeight: 600, letterSpacing: "0.06em",
                    cursor: "default",
                  }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1v7M4 5l3 3 3-3M2 10v2a1 1 0 001 1h8a1 1 0 001-1v-2" stroke={BRAND.gold} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    Certificate available upon request
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Contact note */}
          <div style={{
            marginTop: 56,
            background: BRAND.white,
            borderRadius: 16,
            border: `1px solid ${BRAND.lightGray}`,
            padding: "32px 40px",
            display: "flex", alignItems: "center", gap: 24,
            flexWrap: "wrap",
          }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(0,53,122,0.07)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={BRAND.navy} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 15, color: BRAND.navy, marginBottom: 4 }}>
                Request Certificates or Ask About Compliance
              </div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: BRAND.textMid }}>
                Contact our Compliance Officer at <strong style={{ color: BRAND.navy }}>info@labarium.com</strong> or call <strong style={{ color: BRAND.navy }}>(850) 460-1484</strong> / Toll-Free <strong style={{ color: BRAND.navy }}>(877) 765-2274</strong>. Additional lab locations and their respective licenses will be added here as the Labarium network grows.
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
